import { useState, useEffect, useCallback } from 'react';
import * as crmApi from '../utils/crmApi';

const CRM_QUERY_CACHE_TTL_MS = 30 * 1000;
const CRM_QUERY_CACHE_MAX_ENTRIES = 200;
const crmQueryCache = new Map();

function cacheKeyFor(scope, input) {
    let serialized = '';
    try {
        serialized = JSON.stringify(input ?? null);
    } catch {
        serialized = String(input ?? '');
    }
    return `${scope}:${serialized}`;
}

function readCrmQueryCache(key) {
    const entry = crmQueryCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.ts > CRM_QUERY_CACHE_TTL_MS) {
        crmQueryCache.delete(key);
        return null;
    }
    return entry.value;
}

function writeCrmQueryCache(key, value) {
    crmQueryCache.set(key, { value, ts: Date.now() });
    while (crmQueryCache.size > CRM_QUERY_CACHE_MAX_ENTRIES) {
        const oldestKey = crmQueryCache.keys().next().value;
        if (!oldestKey) break;
        crmQueryCache.delete(oldestKey);
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useDashboard(initialParams = {}) {
    const initialCachedStats = readCrmQueryCache(cacheKeyFor('dashboard', initialParams));
    const [stats, setStats] = useState(initialCachedStats);
    const [loading, setLoading] = useState(!initialCachedStats);
    const [error, setError] = useState(null);
    const [params, setParams] = useState(initialParams);
    const dashboardCacheKey = cacheKeyFor('dashboard', params);

    const fetchStats = useCallback(async (options = {}) => {
        const silent = Boolean(options?.silent);
        const force = Boolean(options?.force);
        const cached = force ? null : readCrmQueryCache(dashboardCacheKey);
        if (cached) {
            setStats(cached);
            setLoading(false);
        } else if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await crmApi.getDashboardStats(params, {
                crmNetworkSilent: silent || Boolean(cached),
            });
            writeCrmQueryCache(dashboardCacheKey, data);
            setStats(data);
            setError(null);
        } catch (err) {
            if (!cached) {
                setError(err.message || 'Failed to fetch dashboard stats');
            }
        } finally {
            if (!silent || !cached) {
                setLoading(false);
            }
        }
    }, [dashboardCacheKey, params]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const updateParams = useCallback((newParams) => {
        setParams(prev => ({ ...prev, ...newParams }));
    }, []);

    return { stats, loading, error, params, updateParams, refetch: fetchStats };
}

// ─────────────────────────────────────────────────────────────────────────────
// Leads Hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useLeads(initialParams = {}) {
    const initialCachedLeads = readCrmQueryCache(cacheKeyFor('leads', initialParams));
    const [leads, setLeads] = useState(initialCachedLeads?.items || []);
    const [total, setTotal] = useState(initialCachedLeads?.total || 0);
    const [totalsByStatus, setTotalsByStatus] = useState(initialCachedLeads?.totalsByStatus || null);
    const [loading, setLoading] = useState(!initialCachedLeads);
    const [error, setError] = useState(null);
    const [params, setParams] = useState(initialParams);
    const leadsCacheKey = cacheKeyFor('leads', params);

    const fetchLeads = useCallback(async (options = {}) => {
        const silent = Boolean(options?.silent);
        const force = Boolean(options?.force);
        const cached = force ? null : readCrmQueryCache(leadsCacheKey);
        if (cached) {
            setLeads(cached.items || []);
            setTotal(cached.total || 0);
            setTotalsByStatus(cached.totalsByStatus || null);
            setLoading(false);
        }
        if (!silent) {
            if (!cached) {
                setLoading(true);
            }
            setError(null);
        }
        try {
            const data = await crmApi.getLeads(params, {
                crmNetworkSilent: silent || Boolean(cached),
            });
            writeCrmQueryCache(leadsCacheKey, {
                items: data.items || [],
                total: data.total || 0,
                totalsByStatus: data.totalsByStatus || null,
            });
            setLeads(data.items || []);
            setTotal(data.total || 0);
            setTotalsByStatus(data.totalsByStatus || null);
            setError(null);
        } catch (err) {
            if (!silent && !cached) {
                setError(err.message || 'Failed to fetch leads');
            }
        } finally {
            if (!silent && !cached) {
                setLoading(false);
            }
        }
    }, [leadsCacheKey, params]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    const updateParams = useCallback((newParams) => {
        setParams(prev => ({ ...prev, ...newParams }));
    }, []);

    return { leads, total, totalsByStatus, loading, error, params, updateParams, refetch: fetchLeads };
}

export function useLead(id) {
    const initialCachedLead = readCrmQueryCache(cacheKeyFor('lead', id || ''));
    const [lead, setLead] = useState(initialCachedLead);
    const [loading, setLoading] = useState(Boolean(id) && !initialCachedLead);
    const [error, setError] = useState(null);
    const leadCacheKey = cacheKeyFor('lead', id || '');

    const fetchLead = useCallback(async (options = {}) => {
        if (!id) return;
        const silent = Boolean(options?.silent);
        const force = Boolean(options?.force);
        const cached = force ? null : readCrmQueryCache(leadCacheKey);
        if (cached) {
            setLead(cached);
            setLoading(false);
        } else if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await crmApi.getLead(id, {
                crmNetworkSilent: silent || Boolean(cached),
            });
            writeCrmQueryCache(leadCacheKey, data);
            setLead(data);
            setError(null);
        } catch (err) {
            if (!cached) {
                setError(err.message || 'Failed to fetch lead');
            }
        } finally {
            if (!silent || !cached) {
                setLoading(false);
            }
        }
    }, [id, leadCacheKey]);

    useEffect(() => {
        fetchLead();
    }, [fetchLead]);

    return { lead, loading, error, refetch: fetchLead };
}

export function useLeadDetail(id) {
    const initialCachedDetail = readCrmQueryCache(cacheKeyFor('leadDetail', id || ''));
    const [detail, setDetail] = useState(initialCachedDetail);
    const [loading, setLoading] = useState(Boolean(id) && !initialCachedDetail);
    const [error, setError] = useState(null);
    const leadDetailCacheKey = cacheKeyFor('leadDetail', id || '');

    const fetchDetail = useCallback(async (options = {}) => {
        if (!id) return;
        const silent = Boolean(options?.silent);
        const force = Boolean(options?.force);
        const cached = force ? null : readCrmQueryCache(leadDetailCacheKey);
        if (cached) {
            setDetail(cached);
            setLoading(false);
        }
        if (!silent) {
            if (!cached) {
                setLoading(true);
            }
            setError(null);
        }
        try {
            const data = await crmApi.getLeadDetail(id, {
                crmNetworkSilent: silent || Boolean(cached),
            });
            writeCrmQueryCache(leadDetailCacheKey, data);
            setDetail(data);
            setError(null);
        } catch (err) {
            if (!silent && !cached) {
                setError(err.message || 'Failed to fetch lead detail');
            }
        } finally {
            if (!silent && !cached) {
                setLoading(false);
            }
        }
    }, [id, leadDetailCacheKey]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    return { detail, loading, error, refetch: fetchDetail };
}

export function useLeadMatches(id) {
    const [matchesData, setMatchesData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchMatches = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getLeadMatches(id);
            setMatchesData(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch matches');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchMatches();
    }, [fetchMatches]);

    return { matchesData, loading, error, refetch: fetchMatches };
}

export function useLeadMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const create = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.createLead(data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to create lead');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const update = useCallback(async (id, data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.updateLead(id, data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to update lead');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const remove = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.deleteLead(id);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to delete lead');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const convert = useCallback(async (id, options = {}) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.convertLead(id, options);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to convert lead');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const scheduleVisit = useCallback(async (id, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.createSiteVisit(id, payload);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to schedule visit');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const confirmVisit = useCallback(async (id, activityId) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.confirmSiteVisit(id, activityId);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to confirm visit');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const completeVisit = useCallback(async (id, activityId, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.completeSiteVisit(id, activityId, payload);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to complete visit');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const logCommunication = useCallback(async (id, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.logLeadCommunication(id, payload);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to log communication');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const createDealFromMatch = useCallback(async (id, payload) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.createDealFromMatch(id, payload);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to create deal');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const executeAction = useCallback(async (id, payload, idempotencyKey) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.executeLeadAction(id, payload, idempotencyKey);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to execute lead action');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        create,
        update,
        remove,
        convert,
        scheduleVisit,
        confirmVisit,
        completeVisit,
        logCommunication,
        createDealFromMatch,
        executeAction,
        loading,
        error,
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// Contacts Hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useContacts(initialParams = {}) {
    const initialCachedContacts = readCrmQueryCache(cacheKeyFor('contacts', initialParams));
    const [contacts, setContacts] = useState(initialCachedContacts?.items || []);
    const [total, setTotal] = useState(initialCachedContacts?.total || 0);
    const [loading, setLoading] = useState(!initialCachedContacts);
    const [error, setError] = useState(null);
    const [params, setParams] = useState(initialParams);
    const contactsCacheKey = cacheKeyFor('contacts', params);

    useEffect(() => {
        setParams(initialParams);
    }, [JSON.stringify(initialParams)]);

    const fetchContacts = useCallback(async (options = {}) => {
        const silent = Boolean(options?.silent);
        const force = Boolean(options?.force);
        const cached = force ? null : readCrmQueryCache(contactsCacheKey);
        if (cached) {
            setContacts(cached.items || []);
            setTotal(cached.total || 0);
            setLoading(false);
        } else if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await crmApi.getContacts(params, {
                crmNetworkSilent: silent || Boolean(cached),
            });
            writeCrmQueryCache(contactsCacheKey, {
                items: data.items || [],
                total: data.total || 0,
            });
            setContacts(data.items || []);
            setTotal(data.total || 0);
            setError(null);
        } catch (err) {
            if (!cached) {
                setError(err.message || 'Failed to fetch contacts');
            }
        } finally {
            if (!silent || !cached) {
                setLoading(false);
            }
        }
    }, [contactsCacheKey, params]);

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    const updateParams = useCallback((newParams) => {
        setParams(prev => ({ ...prev, ...newParams }));
    }, []);

    return { contacts, total, loading, error, params, updateParams, refetch: fetchContacts };
}

export function useContact(id) {
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchContact = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getContact(id);
            setContact(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch contact');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchContact();
    }, [fetchContact]);

    return { contact, loading, error, refetch: fetchContact };
}

export function useContactMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const create = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.createContact(data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to create contact');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const update = useCallback(async (id, data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.updateContact(id, data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to update contact');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const remove = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.deleteContact(id);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to delete contact');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return { create, update, remove, loading, error };
}

// ─────────────────────────────────────────────────────────────────────────────
// Deals Hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useDeals(initialParams = {}) {
    const initialCachedDeals = readCrmQueryCache(cacheKeyFor('deals', initialParams));
    const [deals, setDeals] = useState(initialCachedDeals?.items || []);
    const [total, setTotal] = useState(initialCachedDeals?.total || 0);
    const [loading, setLoading] = useState(!initialCachedDeals);
    const [error, setError] = useState(null);
    const [params, setParams] = useState(initialParams);
    const dealsCacheKey = cacheKeyFor('deals', params);

    useEffect(() => {
        setParams(initialParams);
    }, [JSON.stringify(initialParams)]);

    const fetchDeals = useCallback(async (options = {}) => {
        const silent = Boolean(options?.silent);
        const force = Boolean(options?.force);
        const cached = force ? null : readCrmQueryCache(dealsCacheKey);
        if (cached) {
            setDeals(cached.items || []);
            setTotal(cached.total || 0);
            setLoading(false);
        } else if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await crmApi.getDeals(params, {
                crmNetworkSilent: silent || Boolean(cached),
            });
            writeCrmQueryCache(dealsCacheKey, {
                items: data.items || [],
                total: data.total || 0,
            });
            setDeals(data.items || []);
            setTotal(data.total || 0);
            setError(null);
        } catch (err) {
            if (!cached) {
                setError(err.message || 'Failed to fetch deals');
            }
        } finally {
            if (!silent || !cached) {
                setLoading(false);
            }
        }
    }, [dealsCacheKey, params]);

    useEffect(() => {
        fetchDeals();
    }, [fetchDeals]);

    const updateParams = useCallback((newParams) => {
        setParams(prev => ({ ...prev, ...newParams }));
    }, []);

    return { deals, total, loading, error, params, updateParams, refetch: fetchDeals };
}

export function useDealsPipeline(initialParams = {}) {
    const initialCachedPipeline = readCrmQueryCache(cacheKeyFor('dealsPipeline', initialParams));
    const [pipeline, setPipeline] = useState(initialCachedPipeline);
    const [loading, setLoading] = useState(!initialCachedPipeline);
    const [error, setError] = useState(null);
    const [params, setParams] = useState(initialParams);
    const pipelineCacheKey = cacheKeyFor('dealsPipeline', params);

    useEffect(() => {
        setParams(initialParams);
    }, [JSON.stringify(initialParams)]);

    const fetchPipeline = useCallback(async (options = {}) => {
        const silent = Boolean(options?.silent);
        const force = Boolean(options?.force);
        const cached = force ? null : readCrmQueryCache(pipelineCacheKey);
        if (cached) {
            setPipeline(cached);
            setLoading(false);
        } else if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await crmApi.getDealsPipeline(params, {
                crmNetworkSilent: silent || Boolean(cached),
            });
            writeCrmQueryCache(pipelineCacheKey, data);
            setPipeline(data);
            setError(null);
        } catch (err) {
            if (!cached) {
                setError(err.message || 'Failed to fetch pipeline');
            }
        } finally {
            if (!silent || !cached) {
                setLoading(false);
            }
        }
    }, [params, pipelineCacheKey]);

    useEffect(() => {
        fetchPipeline();
    }, [fetchPipeline]);

    const updateParams = useCallback((newParams) => {
        setParams(prev => ({ ...prev, ...newParams }));
    }, []);

    return { pipeline, loading, error, params, updateParams, refetch: fetchPipeline };
}

export function useDealsDueBuckets(initialParams = {}) {
    const initialCachedBuckets = readCrmQueryCache(cacheKeyFor('dealsDueBuckets', initialParams));
    const [buckets, setBuckets] = useState(initialCachedBuckets);
    const [loading, setLoading] = useState(!initialCachedBuckets);
    const [error, setError] = useState(null);
    const [params, setParams] = useState(initialParams);
    const bucketsCacheKey = cacheKeyFor('dealsDueBuckets', params);

    useEffect(() => {
        setParams(initialParams);
    }, [JSON.stringify(initialParams)]);

    const fetchBuckets = useCallback(async (options = {}) => {
        const silent = Boolean(options?.silent);
        const force = Boolean(options?.force);
        const cached = force ? null : readCrmQueryCache(bucketsCacheKey);
        if (cached) {
            setBuckets(cached);
            setLoading(false);
        } else if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await crmApi.getDealsDueBuckets(params, {
                crmNetworkSilent: silent || Boolean(cached),
            });
            writeCrmQueryCache(bucketsCacheKey, data);
            setBuckets(data);
            setError(null);
        } catch (err) {
            if (!cached) {
                setError(err.message || 'Failed to fetch deal buckets');
            }
        } finally {
            if (!silent || !cached) {
                setLoading(false);
            }
        }
    }, [bucketsCacheKey, params]);

    useEffect(() => {
        fetchBuckets();
    }, [fetchBuckets]);

    const updateParams = useCallback((newParams) => {
        setParams(prev => ({ ...prev, ...newParams }));
    }, []);

    return { buckets, loading, error, params, updateParams, refetch: fetchBuckets };
}

export function useDeal(id) {
    const initialCachedDeal = readCrmQueryCache(cacheKeyFor('deal', id || ''));
    const [deal, setDeal] = useState(initialCachedDeal);
    const [loading, setLoading] = useState(Boolean(id) && !initialCachedDeal);
    const [error, setError] = useState(null);
    const dealCacheKey = cacheKeyFor('deal', id || '');

    const fetchDeal = useCallback(async (options = {}) => {
        if (!id) return;
        const silent = Boolean(options?.silent);
        const force = Boolean(options?.force);
        const cached = force ? null : readCrmQueryCache(dealCacheKey);
        if (cached) {
            setDeal(cached);
            setLoading(false);
        } else if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const data = await crmApi.getDeal(id, {
                crmNetworkSilent: silent || Boolean(cached),
            });
            writeCrmQueryCache(dealCacheKey, data);
            setDeal(data);
            setError(null);
        } catch (err) {
            if (!cached) {
                setError(err.message || 'Failed to fetch deal');
            }
        } finally {
            if (!silent || !cached) {
                setLoading(false);
            }
        }
    }, [dealCacheKey, id]);

    useEffect(() => {
        fetchDeal();
    }, [fetchDeal]);

    return { deal, loading, error, refetch: fetchDeal };
}

export function useDealMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const create = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.createDeal(data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to create deal');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const update = useCallback(async (id, data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.updateDeal(id, data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to update deal');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateStage = useCallback(async (id, stage) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.updateDeal(id, { stage });
            return result;
        } catch (err) {
            setError(err.message || 'Failed to update deal stage');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const remove = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.deleteDeal(id);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to delete deal');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return { create, update, updateStage, remove, loading, error };
}

// ─────────────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────────────
// Properties Hooks
// ────────────────────────────────────────────────────────────────────────────────

export function useProperties(initialParams = {}) {
    const [properties, setProperties] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [params, setParams] = useState(initialParams);

    useEffect(() => {
        setParams(initialParams);
    }, [JSON.stringify(initialParams)]);

    const fetchProperties = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getProperties(params);
            setProperties(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            setError(err.message || 'Failed to fetch properties');
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => {
        fetchProperties();
    }, [fetchProperties]);

    const updateParams = useCallback((newParams) => {
        setParams(prev => ({ ...prev, ...newParams }));
    }, []);

    return { properties, total, loading, error, params, updateParams, refetch: fetchProperties };
}

export function useProperty(id) {
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchProperty = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getProperty(id);
            setProperty(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch property');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchProperty();
    }, [fetchProperty]);

    return { property, loading, error, refetch: fetchProperty };
}

export function usePropertyMatches(id) {
    const [matchesData, setMatchesData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchMatches = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getPropertyMatches(id);
            setMatchesData(data);
        } catch (err) {
            setError(err.message || 'Failed to fetch matches');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchMatches();
    }, [fetchMatches]);

    return { matchesData, loading, error, refetch: fetchMatches };
}

export function usePropertyMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const create = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.createProperty(data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to create property');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const update = useCallback(async (id, data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.updateProperty(id, data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to update property');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const remove = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.deleteProperty(id);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to delete property');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return { create, update, remove, loading, error };
}

// Activities Hooks
// ─────────────────────────────────────────────────────────────────────────────

export function useActivities(initialParams = {}) {
    const [activities, setActivities] = useState([]);
    const [total, setTotal] = useState(0);
    const [summary, setSummary] = useState({ overdue: 0, today: 0, completedToday: 0 });
    const [todayGroups, setTodayGroups] = useState({ overdue: [], today: [], upcoming: [] });
    const [agendaGroups, setAgendaGroups] = useState([]);
    const [calendarMap, setCalendarMap] = useState({});
    const [completedActivities, setCompletedActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [params, setParams] = useState(initialParams);

    useEffect(() => {
        setParams(initialParams);
    }, [JSON.stringify(initialParams)]);

    const fetchActivities = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getActivities(params);
            setActivities(data.items || []);
            setTotal(data.total || 0);
            setSummary(data.summary || { overdue: 0, today: 0, completedToday: 0 });
            setTodayGroups(data.todayGroups || { overdue: [], today: [], upcoming: [] });
            setAgendaGroups(data.agendaGroups || []);
            setCalendarMap(data.calendarMap || {});
            setCompletedActivities(data.completed || []);
        } catch (err) {
            setError(err.message || 'Failed to fetch activities');
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => {
        fetchActivities();
    }, [fetchActivities]);

    const updateParams = useCallback((newParams) => {
        setParams(prev => ({ ...prev, ...newParams }));
    }, []);

    return {
        activities,
        total,
        summary,
        todayGroups,
        agendaGroups,
        calendarMap,
        completedActivities,
        loading,
        error,
        params,
        updateParams,
        refetch: fetchActivities,
    };
}

export function useLeadFollowups(initialParams = {}) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [params, setParams] = useState(initialParams);

    const fetchFollowups = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getLeadFollowups(params);
            setLeads(data.data || []);
        } catch (err) {
            setError(err.message || 'Failed to fetch follow-ups');
        } finally {
            setLoading(false);
        }
    }, [params]);

    useEffect(() => {
        fetchFollowups();
    }, [fetchFollowups]);

    const updateParams = useCallback((newParams) => {
        setParams((prev) => ({ ...prev, ...newParams }));
    }, []);

    return { leads, loading, error, params, updateParams, refetch: fetchFollowups };
}

export function useActivityMutations() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const create = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.createActivity(data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to create activity');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const createLog = useCallback(async (data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.createLogActivity(data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to create log');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const update = useCallback(async (id, data) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.updateActivity(id, data);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to update activity');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const remove = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.deleteActivity(id);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to delete activity');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const complete = useCallback(async (id) => {
        setLoading(true);
        setError(null);
        try {
            const result = await crmApi.completeActivity(id);
            return result;
        } catch (err) {
            setError(err.message || 'Failed to complete activity');
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return { create, createLog, update, remove, complete, loading, error };
}
